/* eslint-env jest */
// NOTE: CI does not run jest yet (#4565) — run locally with `yarn test:js`.
import { clampCenterToBounds, textFieldExtent } from './textFieldGeometry';

describe('clampCenterToBounds', () => {
  // The #4575 fix: horizontal resize in useCanvasDrag must never leave
  // center_x orphaned outside [bound_x_left, bound_x_right] — the production
  // renderer anchors at center_x with no clamp.

  it('leaves a center already inside the bounds untouched', () => {
    expect(clampCenterToBounds(0.5, 0.2, 0.8)).toBe(0.5);
  });

  it('rides the left edge when the left bound passes the center', () => {
    expect(clampCenterToBounds(0.3, 0.4, 0.8)).toBe(0.4);
  });

  it('rides the right edge when the right bound passes the center', () => {
    // The U2027-09 name field: center 0.5047 with bounds 0.609-0.9429 would
    // have been prevented by this clamp.
    expect(clampCenterToBounds(0.9, 0.2, 0.61)).toBe(0.61);
  });

  it('defaults missing bounds to the full canvas (0..1)', () => {
    expect(clampCenterToBounds(0.5, null, undefined)).toBe(0.5);
    expect(clampCenterToBounds(1.2, undefined, undefined)).toBe(1);
  });

  it('passes null/undefined center through (nothing to clamp)', () => {
    expect(clampCenterToBounds(null, 0.2, 0.8)).toBeNull();
    expect(clampCenterToBounds(undefined, 0.2, 0.8)).toBeUndefined();
  });
});

describe('textFieldExtent', () => {
  // Powers the editor's visible overflow warning that replaced the silent
  // ctx.clip() — the editor must show text where production will draw it.

  it('centers the extent on centerX for center align', () => {
    const e = textFieldExtent({
      align: 'center', centerX: 100, left: 50, right: 150, textWidth: 60,
    });
    expect(e.textLeft).toBe(70);
    expect(e.textRight).toBe(130);
    expect(e.overflowsBounds).toBe(false);
  });

  it('flags overflow when a centered text is wider than its box allows', () => {
    const e = textFieldExtent({
      align: 'center', centerX: 140, left: 50, right: 150, textWidth: 60,
    });
    expect(e.textRight).toBe(170);
    expect(e.overflowsBounds).toBe(true);
  });

  it('flags the U2027-08 case: center_x at the right bound pushes half the text past the box', () => {
    // cx == bound_x_right (0.9341 ≈ 2802px on a 3000px canvas, ~968px bounds)
    const e = textFieldExtent({
      align: 'center', centerX: 2802, left: 1818, right: 2802, textWidth: 968,
    });
    expect(e.textRight).toBe(3286);
    expect(e.overflowsBounds).toBe(true);
  });

  it('flags a center_x fully outside the bounds (the U2027-09 mid-canvas name)', () => {
    const e = textFieldExtent({
      align: 'center', centerX: 1514, left: 1827, right: 2829, textWidth: 400,
    });
    expect(e.overflowsBounds).toBe(true);
  });

  it('anchors left/right aligned text at the bound, which never overflows on that side', () => {
    const l = textFieldExtent({
      align: 'left', centerX: 999, left: 50, right: 150, textWidth: 60,
    });
    expect(l.textLeft).toBe(52);
    expect(l.overflowsBounds).toBe(false);

    const r = textFieldExtent({
      align: 'right', centerX: 0, left: 50, right: 150, textWidth: 60,
    });
    expect(r.textRight).toBe(148);
    expect(r.overflowsBounds).toBe(false);
  });

  it('flags aligned text whose WIDTH exceeds the box', () => {
    const e = textFieldExtent({
      align: 'left', centerX: 0, left: 50, right: 150, textWidth: 200,
    });
    expect(e.overflowsBounds).toBe(true);
  });

  it('tolerates the 2px align inset and antialiasing slop', () => {
    const e = textFieldExtent({
      align: 'center', centerX: 100, left: 50, right: 150, textWidth: 102,
    });
    expect(e.overflowsBounds).toBe(false);
  });
});
